/**
 * /thank-you-employer — confirmation + conversion tracking hook.
 *
 * The root element carries data-conversion="employer_signup_completed" so
 * Meta Pixel / Google Ads containers can fire the CompleteRegistration /
 * Lead event on page load without extra JS.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Sparkles, MailCheck, Clock, Calendar } from "lucide-react";

const NEXT_STEPS = [
    { icon: Sparkles, title: "Simonara AI is analyzing your role", body: "Parsing skills, seniority, and match signals. Your AI Hiring Brief will be ready in the workspace." },
    { icon: MailCheck, title: "Confirmation email on its way", body: "Check your inbox for the setup link — you'll create your employer profile and unlock the shortlist." },
    { icon: Clock, title: "First shortlist within 24 hours", body: "Expect five verified Talent Passports matched to your role — usually much faster." },
];

export default function ThankYouEmployer() {
    // Fire a lightweight custom conversion event so tag managers (GTM, etc.)
    // can key off it in addition to the root data attribute.
    useEffect(() => {
        try {
            if (typeof window !== "undefined" && typeof window.dataLayer !== "undefined") {
                window.dataLayer.push({ event: "employer_signup_completed" });
            }
            if (typeof window !== "undefined" && typeof window.fbq !== "undefined") {
                window.fbq("track", "Lead", { content_name: "employer_signup" });
            }
        } catch { /* ignore analytics failures */ }
    }, []);

    return (
        <div
            className="bg-background"
            data-testid="thank-you-employer-page"
            data-conversion="employer_signup_completed"
        >
            <Helmet>
                <title>Job received · Simonara Talent Solutions</title>
                <meta name="robots" content="noindex" />
                <link rel="canonical" href="/thank-you-employer" />
            </Helmet>

            <section className="container-page py-24 max-w-3xl mx-auto text-center">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                    <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle2 size={32} className="text-emerald-500" />
                    </div>
                    <p className="overline mt-6">Confirmed</p>
                    <h1 className="mt-2 font-heading font-semibold text-4xl sm:text-5xl tracking-tight text-foreground">
                        Job received — the AI is on it.
                    </h1>
                    <p className="mt-4 text-lg text-muted-foreground">
                        Thanks for putting your role in front of the Simonara network. Here's what happens next.
                    </p>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
                        {NEXT_STEPS.map((s) => (
                            <div key={s.title} className="card-soft p-5">
                                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                                    <s.icon size={16} />
                                </div>
                                <div className="mt-3 font-heading font-semibold text-foreground text-sm">{s.title}</div>
                                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link
                            to="/signup?role=employer"
                            className="btn-green inline-flex items-center gap-2"
                            data-testid="thank-you-employer-signup"
                        >
                            Finish creating your workspace <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/pricing"
                            className="btn-outline inline-flex items-center gap-2"
                            data-testid="thank-you-employer-pricing"
                        >
                            <Calendar size={14} /> See pricing
                        </Link>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}
